"use client";
/* -------------------------------------------------------------------------- */
/*  CalendarDragContext                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Drag & drop rescheduling for the calendar (desktop only).
 *
 * dnd-kit supplies the sensors, the floating card and auto-scroll; the calendar supplies
 * the geometry, because a column is a continuous timeline rather than a list of slots.
 * Where a card lands is therefore computed here, from the pointer and the target column's
 * live rect, and snapped to the quarter-hour grid.
 *
 * Two contexts on purpose: registration/actions barely change, while the landing preview
 * changes on every pointer move. Splitting them keeps a drag from re-rendering every
 * column and card several times a second — only the small ghost leaves re-render.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";

import type { Appointment } from "@/lib/types";
import { AppointmentCard } from "@/components/appointment-card";
import { useMoveAppointment } from "@/hooks/calendar/useMoveAppointment";
import { useMasterServices } from "@/hooks/calendar/useMasterServices";
import {
  clampStartToDay,
  readHourPx,
  startMinFromTop,
  topPxForMin,
} from "@/lib/calendar/dragGeometry";
import {
  validateMove,
  type MoveRejection,
  type OccupiedSlot,
  type WorkInterval,
} from "@/lib/calendar/moveValidation";

/** How long a card must hover a pager arrow before the calendar turns the page. */
const PAGE_FLIP_MS = 600;
/** A click arriving this soon after a drag is the tail of that drag, not a click. */
const CLICK_SUPPRESSION_MS = 250;

/* ------------------------------- payloads --------------------------------- */

type ColumnPayload = {
  kind: "column";
  columnId: string;
  staffId: number;
  staffName?: string;
  date: string;
  intervals: WorkInterval[];
  occupied: OccupiedSlot[];
  dayStartMin: number;
  dayEndMin: number;
  element: HTMLElement | null;
};

type DragPayload = {
  kind: "appointment";
  appointment: Appointment;
  appointmentId: number;
  staffId: number;
  date: string;
  startMin: number;
  durationMin: number;
  serviceIds: number[];
};

export type DragGhost = {
  columnId: string;
  startMin: number;
  durationMin: number;
  valid: boolean;
  reason?: MoveRejection;
};

export const columnId = (date: string, staffId: number) => `col:${date}:${staffId}`;

/* ------------------------------- contexts --------------------------------- */

type DragActions = {
  enabled: boolean;
  /** Id of the appointment currently being dragged, if any. */
  activeAppointmentId: number | null;
  registerColumn: (id: string, payload: ColumnPayload) => void;
  unregisterColumn: (id: string) => void;
  /** True when a drag ended moments ago, so the trailing click can be swallowed. */
  wasJustDragged: () => boolean;
};

const ActionsContext = createContext<DragActions | null>(null);
const GhostContext = createContext<DragGhost | null>(null);

export function useCalendarDragActions(): DragActions {
  return (
    useContext(ActionsContext) ?? {
      enabled: false,
      activeAppointmentId: null,
      registerColumn: () => {},
      unregisterColumn: () => {},
      wasJustDragged: () => false,
    }
  );
}

/** The landing preview for one column, or null when the card is elsewhere. */
export function useColumnGhost(id: string): DragGhost | null {
  const ghost = useContext(GhostContext);
  return ghost && ghost.columnId === id ? ghost : null;
}

/* ------------------------- collision detection ---------------------------- */

const isInside = (point: { x: number; y: number }, rect: { left: number; right: number; top: number; bottom: number }) =>
  point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

/**
 * Picks the column under the pointer, or a pager arrow when one is hovered.
 *
 * Columns match on horizontal position alone: vertical position selects the *time*, not
 * the column, and a card held slightly above or below the grid should still resolve to
 * the column it is over rather than to nothing.
 */
const calendarCollision: CollisionDetection = ({ droppableContainers, droppableRects, pointerCoordinates, collisionRect }) => {
  const point = pointerCoordinates ?? {
    x: collisionRect.left + collisionRect.width / 2,
    y: collisionRect.top,
  };

  for (const container of droppableContainers) {
    if (container.data.current?.kind !== "pager") continue;
    const rect = droppableRects.get(container.id);
    if (rect && isInside(point, rect)) {
      return [{ id: container.id, data: { droppableContainer: container, value: 1 } }];
    }
  }

  for (const container of droppableContainers) {
    if (container.data.current?.kind !== "column") continue;
    const rect = droppableRects.get(container.id);
    if (rect && point.x >= rect.left && point.x <= rect.right) {
      return [{ id: container.id, data: { droppableContainer: container, value: 1 } }];
    }
  }

  return [];
};

/* -------------------------------- provider -------------------------------- */

type ProviderProps = {
  /** Desktop-only feature: callers pass false on mobile or without edit rights. */
  enabled: boolean;
  /** Turns the staff pager while a card hovers an arrow. Omit where there is no pager. */
  onPageStep?: (direction: 1 | -1) => void;
  children: React.ReactNode;
};

export function CalendarDragProvider({ enabled, onPageStep, children }: ProviderProps) {
  const { t } = useTranslation();
  const move = useMoveAppointment();
  const masterServices = useMasterServices();

  const columnsRef = useRef(new Map<string, ColumnPayload>());
  const dragRef = useRef<DragPayload | null>(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const grabOffsetRef = useRef(0);
  const hourPxRef = useRef(70);
  const ghostRef = useRef<DragGhost | null>(null);
  const overIdRef = useRef<string | null>(null);
  const pagerRef = useRef<{ direction: 0 | 1 | -1; timer: number | null }>({ direction: 0, timer: null });
  const dragEndedAtRef = useRef(0);

  const [ghost, setGhost] = useState<DragGhost | null>(null);
  const [activeDrag, setActiveDrag] = useState<DragPayload | null>(null);

  const pointerSensors = useSensors(
    // The distance threshold is what keeps a click on a card opening the modal: below it
    // dnd-kit never starts a drag and the click passes through untouched.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  // An explicit empty list, not `undefined`: dnd-kit falls back to its own default
  // sensors when the prop is omitted, which would enable dragging on touch devices.
  const noSensors = useSensors();
  const sensors = enabled ? pointerSensors : noSensors;

  const registerColumn = useCallback((id: string, payload: ColumnPayload) => {
    columnsRef.current.set(id, payload);
  }, []);

  const unregisterColumn = useCallback((id: string) => {
    columnsRef.current.delete(id);
  }, []);

  const wasJustDragged = useCallback(
    () => performance.now() - dragEndedAtRef.current < CLICK_SUPPRESSION_MS,
    []
  );

  const stopPager = useCallback(() => {
    if (pagerRef.current.timer != null) window.clearInterval(pagerRef.current.timer);
    pagerRef.current = { direction: 0, timer: null };
  }, []);

  const hoverPager = useCallback(
    (direction: 1 | -1) => {
      if (!onPageStep || pagerRef.current.direction === direction) return;
      stopPager();
      pagerRef.current = {
        direction,
        timer: window.setInterval(() => onPageStep(direction), PAGE_FLIP_MS),
      };
    },
    [onPageStep, stopPager]
  );

  const publishGhost = useCallback((next: DragGhost | null) => {
    const prev = ghostRef.current;
    const same =
      prev === next ||
      (prev != null &&
        next != null &&
        prev.columnId === next.columnId &&
        prev.startMin === next.startMin &&
        prev.valid === next.valid &&
        prev.reason === next.reason);
    if (same) return;
    ghostRef.current = next;
    setGhost(next);
  }, []);

  /** Where the dragged card would land in the given column, and whether it may. */
  const resolveDrop = useCallback(
    (column: ColumnPayload): DragGhost | null => {
      const drag = dragRef.current;
      if (!drag) return null;

      const rect = column.element?.getBoundingClientRect();
      if (!rect) return null;

      // The card's top edge, not the pointer: whatever part of the card was grabbed, it
      // lands where the user sees it.
      const cardTopPx = pointerRef.current.y - grabOffsetRef.current;
      const startMin = clampStartToDay({
        startMin: startMinFromTop({
          cardTopPx,
          overlayTopPx: rect.top,
          hourPx: hourPxRef.current,
          dayStartMin: column.dayStartMin,
        }),
        durationMin: drag.durationMin,
        dayStartMin: column.dayStartMin,
        dayEndMin: column.dayEndMin,
      });

      const sameStaff = column.staffId === drag.staffId;
      const verdict = validateMove({
        startMin,
        durationMin: drag.durationMin,
        intervals: column.intervals,
        occupied: column.occupied,
        movingId: drag.appointmentId,
        requiredServiceIds: drag.serviceIds,
        offeredServiceIds: sameStaff ? [] : masterServices.getLoaded(column.staffId),
        checkServices: !sameStaff,
      });

      return {
        columnId: column.columnId,
        startMin,
        durationMin: drag.durationMin,
        valid: verdict.ok,
        reason: verdict.ok ? undefined : verdict.reason,
      };
    },
    [masterServices]
  );

  const recomputeGhost = useCallback(() => {
    const overId = overIdRef.current;
    if (!overId) return publishGhost(null);
    const column = columnsRef.current.get(overId);
    publishGhost(column ? resolveDrop(column) : null);
  }, [publishGhost, resolveDrop]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const payload = event.active.data.current as DragPayload | undefined;
      if (payload?.kind !== "appointment") return;

      const activator = event.activatorEvent as PointerEvent;
      const initialRect = event.active.rect.current.initial;
      grabOffsetRef.current = initialRect ? activator.clientY - initialRect.top : 0;
      pointerRef.current = { x: activator.clientX, y: activator.clientY };
      hourPxRef.current = readHourPx();
      dragRef.current = payload;
      overIdRef.current = null;
      setActiveDrag(payload);

      // Warm every column the card could reach, so the service check has an answer by the
      // time it is dropped rather than blocking on a request at that moment.
      const staffIds = Array.from(columnsRef.current.values())
        .map((c) => c.staffId)
        .filter((id) => id !== payload.staffId);
      masterServices.prefetchMany(staffIds);
    },
    [masterServices]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const over = event.over;
      const overData = over?.data.current as ColumnPayload | { kind: "pager"; direction: 1 | -1 } | undefined;

      if (overData?.kind === "pager") {
        hoverPager(overData.direction);
        overIdRef.current = null;
        publishGhost(null);
        return;
      }

      stopPager();
      overIdRef.current = overData?.kind === "column" ? overData.columnId : null;
      recomputeGhost();
    },
    [hoverPager, publishGhost, recomputeGhost, stopPager]
  );

  const finishDrag = useCallback(() => {
    stopPager();
    dragEndedAtRef.current = performance.now();
    dragRef.current = null;
    overIdRef.current = null;
    setActiveDrag(null);
    publishGhost(null);
  }, [publishGhost, stopPager]);

  const rejectionMessage = useCallback(
    (reason: MoveRejection) => {
      switch (reason) {
        case "outside_working_hours":
          return t(
            "calendar.drag.outside_working_hours",
            "That time is outside the staff member's working hours"
          );
        case "slot_occupied":
          return t("calendar.drag.slot_occupied", "That time is already taken");
        case "services_unsupported":
          return t(
            "calendar.drag.services_unsupported",
            "This staff member does not provide all of the booked services"
          );
        case "services_unknown":
        default:
          return t(
            "calendar.drag.services_unknown",
            "Could not check this staff member's services. Please try again"
          );
      }
    },
    [t]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const drag = dragRef.current;
      const overData = event.over?.data.current as ColumnPayload | undefined;
      const column = overData?.kind === "column" ? columnsRef.current.get(overData.columnId) : undefined;
      const landing = drag && column ? resolveDrop(column) : null;
      finishDrag();

      if (!drag || !column || !landing) return;

      let verdictValid = landing.valid;
      let reason = landing.reason;

      // The only rejection worth a second look: the service list may simply not have
      // arrived yet. Wait for it once, then decide for real.
      if (!verdictValid && reason === "services_unknown") {
        const offered = await masterServices.ensureLoaded(column.staffId);
        const recheck = validateMove({
          startMin: landing.startMin,
          durationMin: drag.durationMin,
          intervals: column.intervals,
          occupied: column.occupied,
          movingId: drag.appointmentId,
          requiredServiceIds: drag.serviceIds,
          offeredServiceIds: offered,
          checkServices: true,
        });
        verdictValid = recheck.ok;
        reason = recheck.ok ? undefined : recheck.reason;
      }

      if (!verdictValid) {
        toast.error(rejectionMessage(reason ?? "services_unknown"));
        return;
      }

      const unchanged =
        column.staffId === drag.staffId &&
        column.date === drag.date &&
        landing.startMin === drag.startMin;
      if (unchanged) return;

      move.mutate(
        {
          appointmentId: drag.appointmentId,
          date: column.date,
          startMin: landing.startMin,
          durationMin: drag.durationMin,
          staffId: column.staffId,
          staffName: column.staffName,
          fromDate: drag.date,
          fromStaffId: drag.staffId,
        },
        {
          onError: () =>
            toast.error(
              t("calendar.drag.move_failed", "Could not move the appointment. Please try again")
            ),
        }
      );
    },
    [finishDrag, masterServices, move, rejectionMessage, resolveDrop, t]
  );

  // Pointer position drives the landing time, and it has to stay correct while the grid
  // auto-scrolls under a stationary cursor — so it is read from the events themselves
  // rather than from a transform that already folds scrolling in.
  useEffect(() => {
    if (!activeDrag) return;
    const onPointerMove = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      recomputeGhost();
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [activeDrag, recomputeGhost]);

  useEffect(() => stopPager, [stopPager]);

  const actions = useMemo<DragActions>(
    () => ({
      enabled,
      activeAppointmentId: activeDrag?.appointmentId ?? null,
      registerColumn,
      unregisterColumn,
      wasJustDragged,
    }),
    [enabled, activeDrag?.appointmentId, registerColumn, unregisterColumn, wasJustDragged]
  );

  return (
    <ActionsContext.Provider value={actions}>
      <GhostContext.Provider value={ghost}>
        <DndContext
          sensors={sensors}
          collisionDetection={calendarCollision}
          // Columns come and go as the staff pager turns mid-drag, so rects are measured
          // continuously rather than once at drag start.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={finishDrag}
        >
          {children}
          <DragOverlay dropAnimation={null} zIndex={100}>
            {activeDrag ? (
              <div className="h-full w-full opacity-90 shadow-lg cursor-grabbing">
                <AppointmentCard appointment={activeDrag.appointment} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </GhostContext.Provider>
    </ActionsContext.Provider>
  );
}

/* --------------------------------- hooks ---------------------------------- */

/**
 * Registers a column overlay as a drop target.
 *
 * The overlay rather than the column element: its box already excludes the gutters above
 * the first hour and below the last, so its top edge *is* the first drawn minute.
 */
export function useDropColumn(params: {
  staffId: number;
  staffName?: string;
  date: string;
  intervals: WorkInterval[];
  occupied: OccupiedSlot[];
  dayStartMin: number;
  dayEndMin: number;
}) {
  const { enabled, registerColumn, unregisterColumn } = useCalendarDragActions();
  const id = columnId(params.date, params.staffId);
  const elementRef = useRef<HTMLElement | null>(null);

  const payload: ColumnPayload = {
    kind: "column",
    columnId: id,
    staffId: params.staffId,
    staffName: params.staffName,
    date: params.date,
    intervals: params.intervals,
    occupied: params.occupied,
    dayStartMin: params.dayStartMin,
    dayEndMin: params.dayEndMin,
    element: elementRef.current,
  };

  const usable = enabled && Number.isFinite(params.staffId) && params.staffId > 0;
  const { setNodeRef } = useDroppable({ id, data: payload, disabled: !usable });

  // Re-registered on every render so the occupied list and working hours a drop is judged
  // against are the ones currently on screen.
  useEffect(() => {
    if (!usable) return;
    registerColumn(id, { ...payload, element: elementRef.current });
    return () => unregisterColumn(id);
  });

  const ref = useCallback(
    (node: HTMLElement | null) => {
      elementRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef]
  );

  return { columnId: id, setNodeRef: ref };
}

/** Makes one appointment card draggable. */
export function useDraggableAppointment(params: {
  appointment: Appointment;
  staffId: number;
  date: string;
  startMin: number;
  durationMin: number;
}) {
  const { enabled, wasJustDragged } = useCalendarDragActions();
  const appointmentId = Number(params.appointment.id);

  const payload: DragPayload = {
    kind: "appointment",
    appointment: params.appointment,
    appointmentId,
    staffId: params.staffId,
    date: params.date,
    startMin: params.startMin,
    durationMin: params.durationMin,
    serviceIds: params.appointment.serviceIds ?? [],
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `app:${appointmentId}`,
    data: payload,
    disabled: !enabled,
  });

  // dnd-kit releases the pointer without cancelling the click that follows it, which would
  // open the appointment modal at the end of every drag.
  const onClickCapture = useCallback(
    (e: React.MouseEvent) => {
      if (!wasJustDragged()) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [wasJustDragged]
  );

  return {
    setNodeRef,
    isDragging,
    // Listeners only: dnd-kit's `attributes` would make the card focusable and announce
    // it as a button, but there is no keyboard sensor behind that promise. The
    // roledescription is kept because it costs nothing and describes what a mouse user
    // is about to do.
    dragProps: enabled
      ? { ...listeners, "aria-roledescription": attributes["aria-roledescription"], onClickCapture }
      : {},
  };
}

/** Marks a staff-pager arrow as a hover target that turns the page mid-drag. */
export function usePagerDropZone(direction: 1 | -1) {
  const { enabled, activeAppointmentId } = useCalendarDragActions();
  const { setNodeRef, isOver } = useDroppable({
    id: `pager:${direction}`,
    data: { kind: "pager", direction },
    disabled: !enabled,
  });
  return { setNodeRef, isOver, isDragActive: activeAppointmentId != null };
}

/* --------------------------------- ghost ---------------------------------- */

/**
 * The landing preview drawn inside a column while a card hovers it.
 * A leaf on purpose: it is the only thing that re-renders as the pointer moves.
 */
export const ColumnDragGhost: React.FC<{ columnId: string; dayStartMin: number }> = ({
  columnId: id,
  dayStartMin,
}) => {
  const ghost = useColumnGhost(id);
  if (!ghost) return null;

  const hourPx = readHourPx();
  return (
    <div
      className={[
        "pointer-events-none absolute left-1 right-1 rounded-md border-2 border-dashed z-20",
        ghost.valid ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10",
      ].join(" ")}
      style={{
        top: `${topPxForMin(ghost.startMin, dayStartMin, hourPx)}px`,
        height: `${Math.max(1, (ghost.durationMin / 60) * hourPx)}px`,
      }}
      aria-hidden
    />
  );
};
